import axios from 'axios';
import { mediaUrlService } from './mediaUrlService';
import {
  assertProtectedTransportBoundary,
  captureProtectedTransportBoundary,
  createProtectedObjectUrl,
  releaseProtectedObjectUrl,
  rethrowProtectedTransportRequestError,
} from './protectedTransportBoundary';
import { triggerBrowserDownload } from '../utils/download';
import { isStorageMediaUri } from '../utils/mediaSources';

interface OwnedSegmentDownloadRequest {
  gcsUri: string;
  videoId: string;
  segmentId: string;
  filename: string;
}

class MediaDownloadService {
  async downloadOwnedSegment({
    gcsUri,
    videoId,
    segmentId,
    filename,
  }: OwnedSegmentDownloadRequest): Promise<void> {
    if (!isStorageMediaUri(gcsUri) || !videoId || !segmentId) {
      throw new Error('Segment media is not available for download.');
    }

    const transportBoundary = captureProtectedTransportBoundary();
    try {
      const resolutionContext = {
        videoId,
        segmentId,
      };
      const resolvedUrl = await mediaUrlService.resolveUrl(gcsUri, resolutionContext);
      assertProtectedTransportBoundary(transportBoundary);
      if (!resolvedUrl) {
        throw new Error('Segment media is not available for download.');
      }

      const fetchBlob = (url: string) => axios.get<Blob>(url, {
          responseType: 'blob',
          signal: transportBoundary.signal,
          timeout: 10 * 60 * 1000,
          withCredentials: false,
        });
      let response: Awaited<ReturnType<typeof fetchBlob>>;
      try {
        response = await fetchBlob(resolvedUrl);
      } catch (initialError) {
        assertProtectedTransportBoundary(transportBoundary);
        const replacementUrl = await mediaUrlService.refreshFailedUrl(
          gcsUri,
          resolvedUrl,
          resolutionContext,
        );
        assertProtectedTransportBoundary(transportBoundary);
        if (!replacementUrl) {
          throw initialError;
        }
        response = await fetchBlob(replacementUrl);
      }
      assertProtectedTransportBoundary(transportBoundary);

      const blob = response.data;
      if (!(blob instanceof Blob) || blob.size <= 0) {
        throw new Error('The segment download was empty or invalid.');
      }
      const contentLength = Number(response.headers['content-length']);
      if (
        Number.isFinite(contentLength)
        && contentLength >= 0
        && contentLength !== blob.size
      ) {
        throw new Error('The segment download was incomplete.');
      }

      const objectUrl = createProtectedObjectUrl(transportBoundary, blob);
      try {
        assertProtectedTransportBoundary(transportBoundary);
        triggerBrowserDownload(objectUrl, filename);
      } finally {
        window.setTimeout(() => releaseProtectedObjectUrl(objectUrl), 0);
      }
    } catch (error) {
      return rethrowProtectedTransportRequestError(transportBoundary, error);
    }
  }
}

export const mediaDownloadService = new MediaDownloadService();
